// Exterior proportions from the two user-supplied nozzle / T-holder photos.
// These drawing coordinates preserve the existing scene's hose sockets; they
// are not measured CAD, a thread gauge, a material specification or spray data.
export const microLineNozzleDrawing = Object.freeze({ portOffset: .23, headRadius: .065, headCenterOffset: -.43, exitOffset: -.5334 });

export function createMicroLineNozzleAssembly(THREE, { mesh, material, position = [0, 0, 0], id = "" }) {
  const root = new THREE.Group(), internals = new THREE.Group();
  root.name = "photo-micro-line-t-holder"; internals.name = "photo-micro-line-downward-nozzle";
  root.userData = { evidence: "photo-based-exterior-not-measured-cad", appearance: "silver-colour-not-material-specification" };
  root.add(internals);
  // Both groups share the same origin so the scene can move only internals to
  // its existing cutaway layer without changing any socket or spray position.
  const [x, y, z] = position;
  const silver = material(0xc6cbca, { roughness: .32, metalness: .65 });
  const satin = material(0xb9c0be, { roughness: .52, metalness: .50 });
  const insert = material(0xe6e8e4, { roughness: .56, metalness: .12 });
  const dark = material(0x151919, { roughness: .9, metalness: 0 });
  const namedMesh = (name, geometry, surface, parent, at) => {
    const object = mesh(geometry, surface, parent, at);
    object.name = name; object.userData.nozzleId = id;
    return object;
  };
  const cylinder = (name, radius, height, surface, parent, at, sides = 32) =>
    namedMesh(name, new THREE.CylinderGeometry(radius, radius, height, sides), surface, parent, at);
  const sleeve = (outer, inner, length) => new THREE.LatheGeometry([
    new THREE.Vector2(inner, -length / 2), new THREE.Vector2(outer, -length / 2),
    new THREE.Vector2(outer, length / 2), new THREE.Vector2(inner, length / 2),
    new THREE.Vector2(inner, -length / 2)
  ], 32);

  const holder = cylinder("micro-line-t-holder-central-body", .088, .18, satin, root, [x, y, z]);
  holder.rotation.x = Math.PI / 2;
  const portCollars = {};
  [-1, 1].forEach(sign => {
    const name = sign < 0 ? "negative" : "positive";
    const barrel = cylinder(`micro-line-${name}-pushfit-barrel`, .073, .107, silver, root, [x, y, z + sign * .1425]);
    barrel.rotation.x = Math.PI / 2;
    const neck = cylinder(`micro-line-${name}-collet-neck`, .048, .024, satin, root, [x, y, z + sign * .203]);
    neck.rotation.x = Math.PI / 2;
    const lip = namedMesh(`micro-line-${name}-collet-lip`, sleeve(.077, .032, .018), silver, root, [x, y, z + sign * .221]);
    lip.rotation.x = Math.PI / 2;
    portCollars[name] = lip;
  });

  // The right-angle branch is a continuous adapter, not a tapered spray cone.
  const stem = cylinder("micro-line-adapter-core", .045, .30, silver, internals, [x, y - .17, z]);
  const shoulder = cylinder("micro-line-t-branch-shoulder", .070, .045, silver, internals, [x, y - .075, z]);
  const hexes = [
    cylinder("micro-line-upper-hex-adapter", .073, .065, silver, internals, [x, y - .115, z], 6),
    cylinder("micro-line-lower-hex-adapter", .064, .050, silver, internals, [x, y - .23, z], 6)
  ];
  hexes.forEach(object => { object.rotation.y = Math.PI / 6; });
  const neck = cylinder("micro-line-nozzle-thread-neck", .049, .10, silver, internals, [x, y - .29, z]);
  const threadRings = [-.153, -.161, -.169, -.177, -.185, -.263, -.272, -.281].map((offset, index) => {
    const radius = offset < -.25 ? .049 : .045;
    const ring = namedMesh(`micro-line-adapter-thread-ridge-${index + 1}`, new THREE.TorusGeometry(radius, .0016, 4, 24), silver, internals, [x, y + offset, z]);
    ring.rotation.x = Math.PI / 2;
    return ring;
  });

  const sprayHead = new THREE.Group(); sprayHead.name = "micro-line-two-band-spray-head";
  sprayHead.position.set(x, y + microLineNozzleDrawing.headCenterOffset, z);
  sprayHead.userData = { nozzleId: id, evidence: "photo-based-exterior-not-measured-cad" };
  internals.add(sprayHead);
  const ribbedBand = (name, height, center) => {
    const radius = microLineNozzleDrawing.headRadius;
    const geometry = new THREE.CylinderGeometry(radius, radius, height, 120);
    const vertices = geometry.attributes.position;
    // Axial ribs cut into the silhouette, not painted rings. Their count and
    // depth are illustrative; neither is a measured manufacturing dimension.
    for (let i = 0; i < vertices.count; i += 1) {
      const vx = vertices.getX(i), vz = vertices.getZ(i), distance = Math.hypot(vx, vz);
      if (distance < radius * .5) continue;
      const angle = Math.atan2(vz, vx), groovedRadius = radius - .0035 * (.5 + .5 * Math.cos(angle * 40));
      vertices.setXYZ(i, vx * groovedRadius / distance, vertices.getY(i), vz * groovedRadius / distance);
    }
    geometry.computeVertexNormals();
    const object = namedMesh(name, geometry, silver, sprayHead, [0, center, 0]);
    object.userData.ribCount = 40; object.userData.ribEvidence = "illustrative-photo-detail";
    return object;
  };
  const bands = [ribbedBand("micro-line-rear-axial-rib-band", .09, .045), ribbedBand("micro-line-front-axial-rib-band", .07, -.065)];
  const waist = cylinder("micro-line-smooth-narrow-waist", .054, .03, silver, sprayHead, [0, -.015, 0]);
  const faceRim = cylinder("micro-line-flat-front-rim", .064, .006, silver, sprayHead, [0, -.100, 0]);
  const centerPlate = namedMesh("micro-line-light-central-insert", new THREE.CircleGeometry(.028, 32), insert, sprayHead, [0, -.1032, 0]);
  centerPlate.rotation.x = Math.PI / 2;
  const aperture = namedMesh("micro-line-small-outlet-opening", new THREE.CircleGeometry(.0016, 16), dark, sprayHead, [0, -.1034, 0]);
  aperture.rotation.x = Math.PI / 2;
  aperture.userData.dimensionEvidence = "visible-marker-not-to-scale";
  const ports = {
    negative: new THREE.Vector3(x, y, z - microLineNozzleDrawing.portOffset),
    positive: new THREE.Vector3(x, y, z + microLineNozzleDrawing.portOffset)
  };
  return { root, internals, holder, stem, shoulder, hexes, neck, threadRings, sprayHead, bands, waist, faceRim, centerPlate, aperture, portCollars, ports, direction: new THREE.Vector3(0, -1, 0), statedOrificeMm: .2 };
}
